import {
  ProductMetadatumLevel as Level,
  YearType,
} from '../../Utils';
import {
  getDataStatusSelector as getMainDataSelector,
} from '../fetchedData/countryProductYearByCountry';
import {
  getDataSelector as getLayoutSelector,
} from '../fetchedData/layoutData';
import {
  getDataSelector as getLevelMappingSelector,
  MappingType,
} from '../fetchedData/productLevelMapping';
import {
  getDataSelector as getMetadataSelector,
} from '../fetchedData/productMetadata';
import {
  getDataStatusSelector as getProductYearSelector,
} from '../fetchedData/productYear';

import memoize from '../../memoize';
import {
  IRegistryEntry,
} from '../getWorkerEnhancer';
import {
  IWorkerRootState,
} from '../workerRootState';
import {
  IComputationInput,
} from './compute';
import compute from './compute';
import unmemoizedConvert_PY_DataToMap from './convert_PY_DataToMap';
import {
  IInputFromURLRouting,
  ISuccessfulMergeAction,
  IUIState,
  START_SUBSCRIBING,
  STOP_SUBSCRIBING,
} from './getReducer';
import {
  getInputFromURLRouting,
  getUIState,
  hasMergeAlreadyBeenComputed,
} from './reducer';

type IRelevantStorePart = {
  countryProductYearByCountry: IWorkerRootState['countryProductYearByCountry'],
  productSpaceLayout: IWorkerRootState['productSpaceLayout'],
  productMetadata: IWorkerRootState['productMetadata'],
  productLevelMapping: IWorkerRootState['productLevelMapping'],
  productYear: IWorkerRootState['productYear'],
} & IWorkerRootState['rings']['uiState'] & IInputFromURLRouting;

const getRelevantSubStore = (rootState: IWorkerRootState) => {
  const {
    countryProductYearByCountry,
    productSpaceLayout,
    productMetadata,
    productLevelMapping,
    productYear,
    rings: {uiState},
  } = rootState;
  const relevantPart: IRelevantStorePart = {
    countryProductYearByCountry,
    productSpaceLayout,
    productMetadata,
    productLevelMapping,
    productYear,
    ...uiState,
    ...getInputFromURLRouting(rootState),
  };
  return relevantPart;
};

/* Start of getComputationInput */
const convert_PY_DataToMap = memoize(unmemoizedConvert_PY_DataToMap);
const getLayoutData = getLayoutSelector();
const getMetadata = getMetadataSelector();
const getLevelMapping = getLevelMappingSelector();
const getMainData = getMainDataSelector();
const getProductYear = getProductYearSelector();

const getComputationInput = (rootState: IWorkerRootState): IComputationInput => {
  const {
    chartContainerHeight, chartContainerWidth, centerNode,
    deselectedCategories,
  } = getUIState(rootState);
  const retrieved = getInputFromURLRouting(rootState);
  const {
    productClass, year,
  } = retrieved;
  const country = retrieved.country!;
  const queryLevel = retrieved.queryLevel;
  const yearRequest = {
    type: YearType.Single as YearType.Single,
    year,
  };

  const payload = {country, queryLevel, productClass, year: yearRequest, level: Level.fourDigit};

  const layoutData = getLayoutData(rootState, {productClass});
  const metadata = getMetadata(rootState, {productClass});
  const levelMapping = getLevelMapping(rootState, {productClass, mappingType: MappingType.Hi2Low});
  const mainData = getMainData(rootState, payload);
  const data_PY = convert_PY_DataToMap(getProductYear(rootState, {productClass, year: yearRequest}));

  const result: IComputationInput = {
    chartContainerWidth, chartContainerHeight, centerNode,
    data_PY,
    layoutData, metadata, levelMapping,
    deselectedCategories, mainData,
  };
  return result;
};
/* End of getComputationInput */

const isURLRoutingInputValidForUI = ({country}: IInputFromURLRouting) => (country !== undefined);

const productSpaceRegistry: IRegistryEntry<
  IWorkerRootState,

  typeof START_SUBSCRIBING,
  typeof STOP_SUBSCRIBING,

  IInputFromURLRouting,
  IUIState,

  IRelevantStorePart,
  IComputationInput,
  ISuccessfulMergeAction
> = {
  startListeningActionName: START_SUBSCRIBING,
  stopListeningActionName: STOP_SUBSCRIBING,
  getRelevantSubStore,
  getComputationInput,
  compute,
  isURLRoutingInputValidForUI,
  getURLRoutingInputFromRootState: getInputFromURLRouting,
  getUIStateFromRootState: getUIState,
  hasMergeAlreadyBeenComputed,
};

export default productSpaceRegistry;
